// Sugalarm — device orientation lock (cycle 348).
//
// Single owner of the @capacitor/screen-orientation bridge, following the
// lib/haptics.ts pattern exactly: reached ONLY through the bridge-injected
// `window.Capacitor.Plugins` proxy (the pure-remote webview can't resolve a
// bare `import("@capacitor/screen-orientation")` at runtime — no bundler in
// the webview; this is the same hard rule cycle 138 fixed across every other
// Capacitor plugin call in this app). Two calls, both fire-and-forget and
// NEVER throw:
//
//   - `lockPortrait()` — the app's default. Called once on native-mobile
//     launch (App.tsx) so every screen except the Clock face is
//     portrait-only.
//   - `allowAnyOrientation()` — called by the Clock controller
//     (useAlarmClockMode) the instant clock mode goes active, so
//     PortraitClockFace and the landscape AlarmClockMode face can both
//     render as the device is physically rotated; `lockPortrait()` is
//     called again the instant clock mode exits.
//
// No-ops safely (never throws, resolves immediately) on: web, a
// non-Capacitor browser, a Capacitor build where the plugin isn't
// registered, AND — deliberately — the kiosk variant. The kiosk is a
// physically fixed, Knox-managed landscape appliance; `isNativeKioskShell()`
// is the same signal `lib/capacitor.ts` uses everywhere else to keep kiosk
// off every phone-only code path, so this can never touch it.

import { isCapacitorNative, isNativeKioskShell } from "./capacitor";

type OrientationLockType =
  | "any"
  | "natural"
  | "landscape"
  | "portrait"
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape-primary"
  | "landscape-secondary";

interface ScreenOrientationProxy {
  lock: (opts: { orientation: OrientationLockType }) => Promise<void>;
  unlock: () => Promise<void>;
}

/** Native mobile only — excludes web AND the kiosk Android flavor. */
function isNativeMobile(): boolean {
  return isCapacitorNative() && !isNativeKioskShell();
}

function orientationProxy(): ScreenOrientationProxy | null {
  const cap = (
    window as {
      Capacitor?: {
        Plugins?: { ScreenOrientation?: ScreenOrientationProxy };
      };
    }
  ).Capacitor;
  return cap?.Plugins?.ScreenOrientation ?? null;
}

/** Lock to portrait. The app's default state on native mobile — every
 *  screen except the Clock face. */
export async function lockPortrait(): Promise<void> {
  if (!isNativeMobile()) return;
  try {
    await orientationProxy()?.lock({ orientation: "portrait" });
  } catch {
    // Missing plugin / OS refusal — best-effort, never throws.
  }
}

/** Allow free rotation. Called only while the Clock face is the active
 *  screen, so it can render portrait (PortraitClockFace) or landscape
 *  (the bedside AlarmClockMode face) as the device is physically turned. */
export async function allowAnyOrientation(): Promise<void> {
  if (!isNativeMobile()) return;
  try {
    await orientationProxy()?.unlock();
  } catch {
    // Best-effort, never throws.
  }
}
